const TERM_LABELS: Record<string, string> = {
  facilityName: 'Facility name',
  obligorName: 'Borrower',
  sponsorName: 'Sponsor',
  originatorName: 'Originator',
  sellerName: 'Seller',
  servicerName: 'Servicer',
  backupServicerName: 'Backup servicer',
  adminAgentName: 'Administrative agent',
  collateralType: 'Collateral type',
  abfStructure: 'Facility structure',
  currency: 'Currency',
  totalCommitment: 'Total commitment',
  advanceRatePct: 'Advance rate',
  reserveAmount: 'Contractual reserve',
  financingIndex: 'Financing index',
  spreadBps: 'Credit spread',
  rateFloorPct: 'Rate floor',
  unusedFeeBps: 'Unused fee',
  dayCount: 'Day-count convention',
  closingDate: 'Closing date',
  effectiveDate: 'Effective date',
  maturityDate: 'Maturity date',
  commitmentTerminationDate: 'Commitment termination (revolving period end)',
  commitmentType: 'Commitment type',
  reborrowAllowed: 'Reborrowing permitted',
  amortizationStartDate: 'Amortization period commencement',
  termOutOption: 'Term-out option',
  tickingFeeBps: 'Ticking fee',
  nextBorrowingBaseDate: 'Next borrowing-base date',
  allInRatePct: 'All-in rate',
  drawnAmount: 'Initial drawn amount',
  portfolioId: 'Fund',
  facilityCode: 'Facility code',
  externalId: 'External reference',
};

const TERM_GROUPS: Array<{ name: string; keys: string[] }> = [
  { name: 'Parties and structure', keys: ['facilityName', 'obligorName', 'sponsorName', 'originatorName', 'sellerName', 'servicerName', 'backupServicerName', 'adminAgentName', 'collateralType', 'abfStructure', 'currency'] },
  { name: 'Capacity and collateral support', keys: ['totalCommitment', 'drawnAmount', 'advanceRatePct', 'reserveAmount'] },
  { name: 'Pricing', keys: ['financingIndex', 'spreadBps', 'rateFloorPct', 'allInRatePct', 'unusedFeeBps', 'tickingFeeBps', 'dayCount'] },
  { name: 'Facility structure', keys: ['commitmentType', 'reborrowAllowed', 'termOutOption'] },
  { name: 'Dates', keys: ['closingDate', 'effectiveDate', 'maturityDate', 'commitmentTerminationDate', 'amortizationStartDate', 'nextBorrowingBaseDate'] },
  { name: 'Identity and routing', keys: ['facilityCode', 'portfolioId', 'externalId'] },
];

export const businessLabel = (value: string | null | undefined): string => {
  if (!value) return 'Not specified';
  if (TERM_LABELS[value]) return TERM_LABELS[value];
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
};

export const termGroup = (termKey: string): string =>
  TERM_GROUPS.find(group => group.keys.includes(termKey))?.name || 'Other contractual terms';

export const readJsonValue = (value: unknown): unknown => {
  if (value == null) return null;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return readJsonValue((value as { value: unknown }).value);
  }
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

export const displayContractValue = (value: unknown, presence?: string | null): string => {
  if (String(presence || '').toUpperCase() === 'NOT_STATED') return 'Not stated in agreement';
  if (String(presence || '').toUpperCase() === 'UNKNOWN') return 'Unknown — review required';
  const parsed = readJsonValue(value);
  if (parsed == null || parsed === '') return 'Not specified';
  if (typeof parsed === 'boolean') return parsed ? 'Yes' : 'No';
  if (typeof parsed === 'number') return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(parsed);
  if (typeof parsed === 'object') return 'Structured contract value';
  return String(parsed).replace(/_/g, ' ');
};

export const businessState = (state: string | null | undefined): string => {
  const normalized = String(state || '').toUpperCase();
  if (normalized === 'ACTIVE') return 'Active';
  if (normalized === 'SUPERSEDED') return 'Prior version';
  if (normalized === 'VERIFIED') return 'Ready for activation';
  if (normalized === 'REVIEWED') return 'Exceptions remain';
  if (normalized === 'EXTRACTED') return 'Document review required';
  if (normalized === 'REJECTED') return 'Rejected';
  if (normalized === 'SCHEDULED') return 'Approved, takes effect on its date';
  if (normalized === 'WITHDRAWN') return 'Withdrawn';
  return businessLabel(state);
};
